import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { vacancyService, applicationService, placementDriveService } from '../../services/api';
import { getUserData } from '../../utils/auth';
import './CompanyDashboardHome.css';

function CompanyDashboardHome() {
  const userData = getUserData();
  const companyId = userData?.companyId || userData?.CompanyId;

  const [stats, setStats] = useState({
    vacancies: 0,
    applications: 0,
    shortlisted: 0,
    drives: 0,
  });
  const [recentVacancies, setRecentVacancies] = useState([]);
  const [recentApplications, setRecentApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (companyId) loadData();
    else setLoading(false);
  }, [companyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [vacanciesData, appsData, drivesData] = await Promise.all([
        vacancyService.getVacanciesByCompany(companyId),
        applicationService.getApplicationsByCompany(companyId),
        placementDriveService.GetNewPlacementDrives(),
      ]);
      const vacancies = vacanciesData || [];
      const apps = appsData || [];
      setStats({
        vacancies: vacancies.length,
        applications: apps.length,
        shortlisted: apps.filter((a) => a.IsShortlisted).length,
        drives: (drivesData || []).length,
      });
      setRecentVacancies(vacancies.slice(0, 5));
      setRecentApplications(apps.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');

  if (!companyId) {
    return (
      <div className="company-dashboard-home">
        <p>Company session not found. Please log in again.</p>
      </div>
    );
  }

  const companyName = userData?.name || userData?.Name || 'Company';

  if (loading) {
    return (
      <div className="company-dashboard-home">
        <div className="dashboard-welcome">
          <h1>Welcome, {companyName}</h1>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="company-dashboard-home">
      <div className="dashboard-welcome">
        <h1>Welcome, {companyName}</h1>
        <p>View placement drives, post vacancies, and manage student applications.</p>
      </div>

      <div className="stats-grid">
        <Link to="/company-portal/placement-drives" className="stat-card">
          <span className="stat-icon">📋</span>
          <span className="stat-value">{stats.drives}</span>
          <span className="stat-label">Placement Drives</span>
        </Link>
        <Link to="/company-portal/my-vacancies" className="stat-card">
          <span className="stat-icon">💼</span>
          <span className="stat-value">{stats.vacancies}</span>
          <span className="stat-label">My Vacancies</span>
        </Link>
        <Link to="/company-portal/applications" className="stat-card">
          <span className="stat-icon">📄</span>
          <span className="stat-value">{stats.applications}</span>
          <span className="stat-label">Applications</span>
        </Link>
        <div className="stat-card stat-card-highlight">
          <span className="stat-icon">⭐</span>
          <span className="stat-value">{stats.shortlisted}</span>
          <span className="stat-label">Shortlisted</span>
        </div>
      </div>

      <div className="dashboard-sections">
        <div className="dashboard-section">
          <h3>Recent Vacancies</h3>
          {recentVacancies.length === 0 ? (
            <p className="empty-msg">No vacancies yet. <Link to="/company-portal/post-vacancy">Post a vacancy</Link>.</p>
          ) : (
            <ul className="recent-list">
              {recentVacancies.map((v) => (
                <li key={v.Id}>
                  <Link to={`/company-portal/vacancy/${v.Id}`}>{v.Title}</Link>
                  <span className="date">{formatDate(v.Date)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="dashboard-section">
          <h3>Recent Applications</h3>
          {recentApplications.length === 0 ? (
            <p className="empty-msg">No applications received yet.</p>
          ) : (
            <ul className="recent-list">
              {recentApplications.map((a) => (
                <li key={a.Id}>
                  <span><strong>{a.Student?.Name}</strong> – {a.Vacancy?.Title}</span>
                  <span className={`status ${a.IsShortlisted ? 'shortlisted' : ''}`}>
                    {a.IsShortlisted ? '⭐ Shortlisted' : 'Pending'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default CompanyDashboardHome;
